export const LERA_MODIFIERS = {
  CTRL: 1,
  SHIFT: 2,
  ALT: 4,
} as const

export const LERA_KEYS = {
  UP: 256,
  DOWN: 257,
  LEFT: 258,
  RIGHT: 259,
  HOME: 260,
  END: 261,
  INSERT: 262,
  DELETE: 263,
  PAGE_UP: 264,
  PAGE_DOWN: 265,
  F1: 266,
  F12: 277,
  ESCAPE: 278,
  TAB: 279,
  ENTER: 280,
  BACKSPACE: 281,
} as const

export type GridFontSizeAction = 'increase' | 'decrease' | 'reset'

export interface GridInputTransport {
  sendGridKey(key: number, mods: number): void
  sendGridText(text: string): void
  sendGridFontSize(action: GridFontSizeAction): void
}

export interface GridInputSelection {
  hasSelection(): boolean
  copySelection(): void
  clearSelection(): void
}

export interface GridInputOptions {
  readonly surface: HTMLElement
  readonly transport: GridInputTransport
  readonly enabled?: () => boolean
  readonly selection?: GridInputSelection | null
}

interface GridTextUpdateEvent extends Event {
  readonly text: string
  readonly updateRangeStart: number
  readonly updateRangeEnd: number
}

interface GridCharacterBoundsUpdateEvent extends Event {
  readonly rangeStart: number
  readonly rangeEnd: number
}

interface GridEditContext extends EventTarget {
  readonly text: string
  updateText(rangeStart: number, rangeEnd: number, text: string): void
  updateSelection(start: number, end: number): void
  updateControlBounds(bounds: DOMRect): void
  updateSelectionBounds(bounds: DOMRect): void
  updateCharacterBounds?(rangeStart: number, bounds: DOMRect[]): void
}

interface GridEditContextConstructor {
  new(options?: { text?: string; selectionStart?: number; selectionEnd?: number }): GridEditContext
}

type GridEditableSurface = HTMLElement & { editContext?: GridEditContext | null }

const GRID_TEXT_MAX_UTF8_BYTES = 65_536
const MAX_EDIT_CONTEXT_CHARACTER_BOUNDS = 1_024

const SPECIAL_KEYS: Readonly<Record<string, number>> = {
  ArrowUp: LERA_KEYS.UP,
  ArrowDown: LERA_KEYS.DOWN,
  ArrowLeft: LERA_KEYS.LEFT,
  ArrowRight: LERA_KEYS.RIGHT,
  Home: LERA_KEYS.HOME,
  End: LERA_KEYS.END,
  Insert: LERA_KEYS.INSERT,
  Delete: LERA_KEYS.DELETE,
  PageUp: LERA_KEYS.PAGE_UP,
  PageDown: LERA_KEYS.PAGE_DOWN,
  F1: LERA_KEYS.F1,
  F2: LERA_KEYS.F1 + 1,
  F3: LERA_KEYS.F1 + 2,
  F4: LERA_KEYS.F1 + 3,
  F5: LERA_KEYS.F1 + 4,
  F6: LERA_KEYS.F1 + 5,
  F7: LERA_KEYS.F1 + 6,
  F8: LERA_KEYS.F1 + 7,
  F9: LERA_KEYS.F1 + 8,
  F10: LERA_KEYS.F1 + 9,
  F11: LERA_KEYS.F1 + 10,
  F12: LERA_KEYS.F12,
  Escape: LERA_KEYS.ESCAPE,
  Tab: LERA_KEYS.TAB,
  Enter: LERA_KEYS.ENTER,
  NumpadEnter: LERA_KEYS.ENTER,
  Backspace: LERA_KEYS.BACKSPACE,
}

const PHYSICAL_PRINTABLE_KEYS: Readonly<Record<string, string>> = {
  Space: ' ',
  Backquote: '`',
  Minus: '-',
  Equal: '=',
  BracketLeft: '[',
  BracketRight: ']',
  Backslash: '\\',
  Semicolon: ';',
  Quote: "'",
  Comma: ',',
  Period: '.',
  Slash: '/',
}

const NO_SELECTION: GridInputSelection = {
  hasSelection: () => false,
  copySelection: () => {},
  clearSelection: () => {},
}

function modifiers(event: KeyboardEvent): number {
  return (event.ctrlKey ? LERA_MODIFIERS.CTRL : 0) |
    (event.shiftKey ? LERA_MODIFIERS.SHIFT : 0) |
    (event.altKey ? LERA_MODIFIERS.ALT : 0)
}

function printableKey(key: string): number | null {
  if (Array.from(key).length !== 1) return null
  let codepoint = key.codePointAt(0)
  if (codepoint === undefined) return null
  if (codepoint >= 65 && codepoint <= 90) codepoint += 32
  return codepoint >= 32 && codepoint <= 126 ? codepoint : null
}

function physicalPrintableKey(code: string): number | null {
  if (/^Key[A-Z]$/u.test(code)) return code.codePointAt(3)! + 32
  if (/^Digit[0-9]$/u.test(code)) return code.codePointAt(5)!
  const key = PHYSICAL_PRINTABLE_KEYS[code]
  return key === undefined ? null : key.codePointAt(0)!
}

function canonicalPrintableKey(event: KeyboardEvent): number | null {
  if (event.ctrlKey || event.altKey) {
    const physical = physicalPrintableKey(event.code)
    if (physical !== null) return physical
  }
  return printableKey(event.key)
}

function printableText(key: string): string | null {
  const scalars = Array.from(key)
  if (scalars.length !== 1) return null
  const codepoint = key.codePointAt(0)
  if (codepoint === undefined || codepoint < 32 ||
      (codepoint >= 127 && codepoint <= 159)) return null
  return key
}

export class GridInput {
  readonly #transport: GridInputTransport
  readonly #enabled: () => boolean
  #selection: GridInputSelection
  #surface: HTMLElement | null = null
  #sink: HTMLTextAreaElement | null = null
  #editContext: GridEditContext | null = null
  #editContextText = ''
  #editContextComposing = false
  #editContextCompositionText = ''
  #editContextCommit: { text: string; sequence: number } | null = null
  #composing = false
  #compositionCommit: { text: string; sequence: number } | null = null
  #inputEventSequence = 0
  #textFallbackGeneration = 0
  #textFallbackCommit: { text: string; sequence: number } | null = null

  readonly #keydown = (event: KeyboardEvent): void => this.#handleKeydown(event)
  readonly #beforeinput = (event: InputEvent): void => this.#handleBeforeInput(event)
  readonly #compositionstart = (): void => {
    ++this.#textFallbackGeneration
    ++this.#inputEventSequence
    this.#composing = true
    this.#compositionCommit = null
  }
  readonly #compositionend = (event: CompositionEvent): void => this.#handleCompositionEnd(event)
  readonly #inputEvent = (): void => {
    this.#advanceInputSequence()
    if (!this.#composing && this.#sink !== null) this.#sink.value = ''
  }
  readonly #paste = (event: ClipboardEvent): void => this.#handlePaste(event)
  // A canvas holding an EditContext never becomes the clipboard event target: the browser
  // dispatches paste at the document selection's focus node instead, so claim it there.
  readonly #documentPaste = (event: ClipboardEvent): void => {
    if (event.target === this.#surface || event.target === this.#sink) return
    if (!this.hasFocus()) return
    this.#handlePaste(event)
  }
  readonly #surfaceFocus = (): void => {
    if (this.#sink !== null &&
        this.#surface?.ownerDocument.activeElement !== this.#sink) {
      this.#sink.focus({ preventScroll: true })
    }
    this.#updateEditContextBounds()
  }
  readonly #updateBounds = (): void => this.#updateEditContextBounds()
  readonly #editContextTextUpdate = (event: Event): void => {
    this.#handleEditContextTextUpdate(event as GridTextUpdateEvent)
  }
  readonly #editContextCompositionStart = (): void => {
    ++this.#textFallbackGeneration
    this.#advanceInputSequence()
    this.#editContextComposing = true
    this.#editContextText = this.#editContext?.text ?? ''
    this.#editContextCompositionText = this.#editContextText
  }
  readonly #editContextCompositionEnd = (event: Event): void => {
    this.#handleEditContextCompositionEnd(event as CompositionEvent)
  }
  readonly #editContextCharacterBounds = (event: Event): void => {
    this.#handleEditContextCharacterBounds(event as GridCharacterBoundsUpdateEvent)
  }

  constructor(options: GridInputOptions) {
    this.#transport = options.transport
    this.#enabled = options.enabled ?? (() => true)
    this.#selection = options.selection ?? NO_SELECTION
    this.attach(options.surface)
  }

  attach(surface: HTMLElement): void {
    if (surface === this.#surface) return
    this.#detach()
    this.#surface = surface
    this.#addInputListeners(surface)
    surface.addEventListener('focus', this.#surfaceFocus)
    surface.ownerDocument.addEventListener('paste', this.#documentPaste as EventListener)
    window.addEventListener('resize', this.#updateBounds)
    window.addEventListener('scroll', this.#updateBounds, true)
    if (!this.#attachEditContext(surface)) this.#attachFallbackSink(surface)
  }

  setSelection(selection: GridInputSelection | null): void {
    this.#selection = selection ?? NO_SELECTION
  }

  focus(): void {
    const target = this.#editContext !== null ? this.#surface : this.#sink
    target?.focus({ preventScroll: true })
    this.#updateEditContextBounds()
  }

  hasFocus(): boolean {
    const activeElement = this.#surface?.ownerDocument.activeElement
    return activeElement === this.#surface || activeElement === this.#sink
  }

  dispose(): void {
    this.#detach()
    this.#surface = null
  }

  #detach(): void {
    if (this.#surface === null) return
    this.#removeInputListeners(this.#surface)
    this.#surface.removeEventListener('focus', this.#surfaceFocus)
    this.#surface.ownerDocument.removeEventListener(
      'paste', this.#documentPaste as EventListener,
    )
    window.removeEventListener('resize', this.#updateBounds)
    window.removeEventListener('scroll', this.#updateBounds, true)
    if (this.#sink !== null) {
      this.#removeInputListeners(this.#sink)
      this.#sink.remove()
      this.#sink = null
    }
    if (this.#editContext !== null) {
      this.#editContext.removeEventListener('textupdate', this.#editContextTextUpdate)
      this.#editContext.removeEventListener('compositionstart', this.#editContextCompositionStart)
      this.#editContext.removeEventListener('compositionend', this.#editContextCompositionEnd)
      this.#editContext.removeEventListener(
        'characterboundsupdate', this.#editContextCharacterBounds,
      )
      const editable = this.#surface as GridEditableSurface
      if (editable.editContext === this.#editContext) editable.editContext = null
      this.#editContext = null
    }
    this.#editContextComposing = false
    this.#editContextText = ''
    this.#editContextCompositionText = ''
    this.#editContextCommit = null
    this.#composing = false
    this.#advanceInputSequence()
    ++this.#textFallbackGeneration
  }

  #addInputListeners(target: HTMLElement): void {
    target.addEventListener('keydown', this.#keydown)
    target.addEventListener('beforeinput', this.#beforeinput as EventListener)
    target.addEventListener('compositionstart', this.#compositionstart)
    target.addEventListener('compositionend', this.#compositionend)
    target.addEventListener('input', this.#inputEvent)
    target.addEventListener('paste', this.#paste as EventListener)
  }

  #removeInputListeners(target: HTMLElement): void {
    target.removeEventListener('keydown', this.#keydown)
    target.removeEventListener('beforeinput', this.#beforeinput as EventListener)
    target.removeEventListener('compositionstart', this.#compositionstart)
    target.removeEventListener('compositionend', this.#compositionend)
    target.removeEventListener('input', this.#inputEvent)
    target.removeEventListener('paste', this.#paste as EventListener)
  }

  #attachEditContext(surface: HTMLElement): boolean {
    const view = surface.ownerDocument.defaultView as unknown as {
      EditContext?: GridEditContextConstructor
    } | null
    if (view?.EditContext === undefined) return false
    let context: GridEditContext | null = null
    try {
      context = new view.EditContext({ text: '', selectionStart: 0, selectionEnd: 0 })
      ;(surface as GridEditableSurface).editContext = context
      if ((surface as GridEditableSurface).editContext !== context) {
        try { (surface as GridEditableSurface).editContext = null } catch {}
        return false
      }
      this.#editContext = context
      this.#editContextText = context.text
      context.addEventListener('textupdate', this.#editContextTextUpdate)
      context.addEventListener('compositionstart', this.#editContextCompositionStart)
      context.addEventListener('compositionend', this.#editContextCompositionEnd)
      context.addEventListener('characterboundsupdate', this.#editContextCharacterBounds)
      this.#updateEditContextBounds()
      return true
    } catch {
      try {
        if ((surface as GridEditableSurface).editContext === context) {
          ;(surface as GridEditableSurface).editContext = null
        }
      } catch {}
      this.#editContext = null
      return false
    }
  }

  #attachFallbackSink(surface: HTMLElement): void {
    const sink = surface.ownerDocument.createElement('textarea')
    sink.dataset.leraGridInput = ''
    sink.tabIndex = -1
    sink.setAttribute('aria-hidden', 'true')
    sink.autocomplete = 'off'
    sink.autocapitalize = 'off'
    sink.spellcheck = false
    Object.assign(sink.style, {
      position: 'fixed', width: '1px', height: '1px', left: '0', bottom: '0',
      padding: '0', border: '0', margin: '0', opacity: '0', overflow: 'hidden',
      pointerEvents: 'none', resize: 'none', zIndex: '-1',
    })
    this.#sink = sink
    this.#addInputListeners(sink)
    ;(surface.parentElement ?? surface.ownerDocument.body).appendChild(sink)
  }

  #handleKeydown(event: KeyboardEvent): void {
    this.#advanceInputSequence()
    if (!this.#enabled() || event.defaultPrevented || event.metaKey || event.isComposing ||
        event.key === 'Process') return
    const mods = modifiers(event)
    const printable = canonicalPrintableKey(event)
    const fallbackText = printableText(event.key)
    if (fallbackText !== null && event.ctrlKey && event.altKey &&
        event.getModifierState('AltGraph')) {
      this.#queueTextFallback(fallbackText)
      return
    }

    if ((mods & LERA_MODIFIERS.CTRL) !== 0 && printable === 'v'.codePointAt(0)) {
      return
    }
    if ((mods & LERA_MODIFIERS.CTRL) !== 0 &&
        printable === 'c'.codePointAt(0) && this.#selection.hasSelection()) {
      event.preventDefault()
      this.#selection.copySelection()
      return
    }
    if (event.key === 'Escape' && this.#selection.hasSelection()) {
      event.preventDefault()
      this.#selection.clearSelection()
      return
    }
    if ((mods & LERA_MODIFIERS.CTRL) !== 0) {
      let action: GridFontSizeAction | null = null
      if (printable === '='.codePointAt(0) || printable === '+'.codePointAt(0)) {
        action = 'increase'
      } else if (printable === '-'.codePointAt(0)) {
        action = 'decrease'
      } else if (printable === '0'.codePointAt(0)) {
        action = 'reset'
      }
      if (action !== null) {
        event.preventDefault()
        this.#transport.sendGridFontSize(action)
        return
      }
    }

    const special = SPECIAL_KEYS[event.code] ?? SPECIAL_KEYS[event.key]
    if (special !== undefined) {
      event.preventDefault()
      this.#transport.sendGridKey(special, mods)
      return
    }
    if (fallbackText !== null &&
        (mods & (LERA_MODIFIERS.CTRL | LERA_MODIFIERS.ALT)) === 0) {
      // A focusable canvas is not an editing host in every browser. Give a
      // browser-generated beforeinput first refusal; if none follows the
      // keydown, deliver the printable scalar as text after the event turn.
      this.#queueTextFallback(fallbackText)
      return
    }
    if (printable !== null && (mods & (LERA_MODIFIERS.CTRL | LERA_MODIFIERS.ALT)) !== 0) {
      event.preventDefault()
      this.#transport.sendGridKey(printable, mods)
    }
  }

  #queueTextFallback(text: string): void {
    const generation = ++this.#textFallbackGeneration
    setTimeout(() => {
      if (generation !== this.#textFallbackGeneration || !this.#enabled()) return
      // The generation guard only covers the ordering where the browser's own
      // input event beats this timer. When the timer wins - which is what a
      // loaded main thread produces, and why the doubling was intermittent -
      // the character is already sent, so the matching input event that lands
      // afterwards has to be suppressed. Same one-event commit contract the
      // composition paths use: any intervening event expires it.
      this.#textFallbackCommit = { text, sequence: this.#inputEventSequence }
      this.#sendText(text)
    }, 0)
  }

  #advanceInputSequence(): number {
    this.#compositionCommit = null
    this.#editContextCommit = null
    this.#textFallbackCommit = null
    return ++this.#inputEventSequence
  }

  #handleBeforeInput(event: InputEvent): void {
    const commit = this.#compositionCommit
    const fallback = this.#textFallbackCommit
    const sequence = this.#advanceInputSequence()
    ++this.#textFallbackGeneration
    if (!this.#enabled() || event.defaultPrevented || this.#composing ||
        event.isComposing || event.inputType === 'insertCompositionText') return
    if (commit !== null) {
      const duplicate = sequence === commit.sequence + 1 &&
        event.data === commit.text &&
        (event.inputType === 'insertFromComposition' || event.inputType === 'insertText')
      if (duplicate) {
        event.preventDefault()
        return
      }
    }
    if (fallback !== null && sequence === fallback.sequence + 1 &&
        event.data === fallback.text &&
        (event.inputType === 'insertText' ||
         event.inputType === 'insertReplacementText')) {
      event.preventDefault()
      return
    }
    let editKey: { key: number; mods: number } | null = null
    switch (event.inputType) {
      case 'deleteContentBackward':
        editKey = { key: LERA_KEYS.BACKSPACE, mods: 0 }
        break
      case 'deleteContentForward':
        editKey = { key: LERA_KEYS.DELETE, mods: 0 }
        break
      case 'insertParagraph':
      case 'insertLineBreak':
        editKey = { key: LERA_KEYS.ENTER, mods: 0 }
        break
    }
    if (editKey !== null) {
      event.preventDefault()
      this.#transport.sendGridKey(editKey.key, editKey.mods)
      return
    }
    if (this.#editContext !== null) return
    if ((event.inputType !== 'insertText' &&
         event.inputType !== 'insertReplacementText' &&
         event.inputType !== 'insertFromComposition') ||
        event.data === null || event.data.length === 0) return
    event.preventDefault()
    this.#sendText(event.data)
  }

  #handleCompositionEnd(event: CompositionEvent): void {
    if (!this.#composing) return
    this.#composing = false
    const sequence = ++this.#inputEventSequence
    if (!this.#enabled() || event.data.length === 0) {
      this.#compositionCommit = null
      return
    }
    // Browsers disagree about whether the final beforeinput precedes or follows
    // compositionend, and the latter may arrive in another task. Suppress only
    // the immediately next matching input event; any intervening key/input or
    // lifecycle event advances the sequence and expires this commit.
    this.#compositionCommit = { text: event.data, sequence }
    this.#sendText(event.data)
    if (this.#sink !== null) this.#sink.value = ''
  }

  #handlePaste(event: ClipboardEvent): void {
    this.#advanceInputSequence()
    if (!this.#enabled() || event.defaultPrevented) return
    const text = event.clipboardData?.getData('text/plain')
    if (text === undefined || text.length === 0) return
    event.preventDefault()
    this.#sendText(text.replace(/\r\n?/gu, '\n'))
  }

  #handleEditContextTextUpdate(event: GridTextUpdateEvent): void {
    const commit = this.#editContextCommit
    const fallback = this.#textFallbackCommit
    ++this.#textFallbackGeneration
    const sequence = this.#advanceInputSequence()
    const rangeStart = Number.isInteger(event.updateRangeStart)
      ? Math.max(0, Math.min(this.#editContextText.length, event.updateRangeStart))
      : 0
    const rangeEnd = Number.isInteger(event.updateRangeEnd)
      ? Math.max(rangeStart, Math.min(this.#editContextText.length, event.updateRangeEnd))
      : this.#editContextText.length
    this.#editContextText = this.#editContextText.slice(0, rangeStart) +
      event.text + this.#editContextText.slice(rangeEnd)
    this.#updateEditContextBounds()
    if (this.#editContextComposing) {
      this.#editContextCompositionText = this.#editContextText
      return
    }
    if (commit !== null && sequence === commit.sequence + 1 &&
        this.#editContextText === commit.text) {
      this.#resetEditContext()
      return
    }
    if (fallback !== null && sequence === fallback.sequence + 1 &&
        this.#editContextText === fallback.text) {
      this.#resetEditContext()
      return
    }
    if (this.#enabled() && this.#editContextText.length > 0) {
      this.#sendText(this.#editContextText)
    }
    this.#resetEditContext()
  }

  #handleEditContextCompositionEnd(event: CompositionEvent): void {
    ++this.#textFallbackGeneration
    this.#advanceInputSequence()
    if (!this.#editContextComposing) return
    this.#editContextComposing = false
    const text = this.#editContextCompositionText ||
      (typeof event.data === 'string' ? event.data : '')
    this.#editContextCompositionText = ''
    if (this.#enabled() && text.length > 0) {
      const sequence = this.#inputEventSequence
      this.#editContextCommit = { text, sequence }
      this.#sendText(text)
    }
    this.#resetEditContext()
  }

  #resetEditContext(): void {
    const context = this.#editContext
    if (context === null) return
    if (context.text.length > 0) context.updateText(0, context.text.length, '')
    context.updateSelection(0, 0)
    this.#editContextText = ''
    this.#updateEditContextBounds()
  }

  #updateEditContextBounds(): void {
    const context = this.#editContext
    const surface = this.#surface
    if (context === null || surface === null) return
    const control = surface.getBoundingClientRect()
    context.updateControlBounds(new DOMRect(
      control.left, control.top, control.width, control.height,
    ))
    context.updateSelectionBounds(new DOMRect(
      control.left, Math.max(control.top, control.bottom - 1), 1, 1,
    ))
  }

  #handleEditContextCharacterBounds(event: GridCharacterBoundsUpdateEvent): void {
    const context = this.#editContext
    const surface = this.#surface
    if (context?.updateCharacterBounds === undefined || surface === null) return
    const control = surface.getBoundingClientRect()
    const caret = new DOMRect(
      control.left, Math.max(control.top, control.bottom - 1), 1, 1,
    )
    const count = Math.max(0, event.rangeEnd - event.rangeStart)
    if (count > MAX_EDIT_CONTEXT_CHARACTER_BOUNDS) return
    context.updateCharacterBounds(
      event.rangeStart, Array.from({ length: count }, () => caret),
    )
  }

  #sendText(text: string): void {
    let chunk = ''
    let chunkBytes = 0
    for (const rawScalar of text) {
      const rawCodepoint = rawScalar.codePointAt(0)!
      const scalar = rawCodepoint >= 0xd800 && rawCodepoint <= 0xdfff
        ? '\ufffd' : rawScalar
      const codepoint = scalar.codePointAt(0)!
      const scalarBytes = codepoint <= 0x7f ? 1
        : codepoint <= 0x7ff ? 2
          : codepoint <= 0xffff ? 3 : 4
      if (chunkBytes + scalarBytes > GRID_TEXT_MAX_UTF8_BYTES) {
        if (chunk.length > 0) this.#transport.sendGridText(chunk)
        chunk = ''
        chunkBytes = 0
      }
      chunk += scalar
      chunkBytes += scalarBytes
    }
    if (chunk.length > 0) this.#transport.sendGridText(chunk)
  }
}
